import React, { useState } from 'react';
import { toast } from 'sonner';
import { ClipboardPaste, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { wpost } from '@/components/website/api';

const COLS = ['customer', 'domain', 'built_on', 'built_by', 'hosting', 'renewal_date', 'status', 'contact_name', 'contact_phone', 'contact_email', 'notes'];
const LABEL = { customer: 'Customer', domain: 'Domain', built_on: 'Built', built_by: 'Built by', hosting: 'Hosting', renewal_date: 'Renewal', status: 'Status', contact_name: 'Contact', contact_phone: 'Phone', contact_email: 'Email', notes: 'Notes' };

/** Paste rows straight from Google Sheets → preview → import. */
export function PasteImportDialog({ open, onClose, onImported }) {
  const [text, setText] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (commit) => {
    setBusy(true);
    try {
      const d = await wpost('/staff/website-log/paste', { text, commit });
      if (commit) { toast.success(`${d.imported} website${d.imported === 1 ? '' : 's'} added to the log`); onImported(); close(); } else setPreview(d);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const close = () => { setText(''); setPreview(null); onClose(); };
  const shown = preview ? COLS.filter((c) => Object.values(preview.columns).includes(c)) : [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="paste-import-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ClipboardPaste className="w-5 h-5 text-teal-700" /> Paste from your spreadsheet</DialogTitle></DialogHeader>
        {!preview ? (
          <>
            <p className="text-sm text-gray-600">In Google Sheets select the rows <strong>including the header row</strong>, copy, and paste below. Columns are matched by their heading (Customer, Domain, Date, Built by, Hosting, Renewal date, Status, Contact, Phone, Email, Notes — any order).</p>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={10} placeholder={'Customer\tDomain\tDate\tHosting\tStatus\tContact\tPhone\tEmail\tNotes\nSmith Driveways\tsmithdriveways.co.uk\t2021-05-01\tFasthosts\tLive\tJohn Smith\t07700 900000\tjohn@...\t'} className="font-mono text-xs" data-testid="paste-textarea" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={close}>Cancel</Button>
              <Button onClick={() => run(false)} disabled={busy || !text.trim()} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="paste-preview-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Preview'}</Button>
            </div>
          </>
        ) : (
          <>
            <div className="text-sm text-gray-700 flex flex-wrap gap-x-4 gap-y-1" data-testid="paste-summary">
              <span><strong>{preview.total}</strong> rows found</span>
              {preview.duplicates > 0 && <span className="text-amber-700"><strong>{preview.duplicates}</strong> already in the log (same domain) — will be skipped</span>}
              {preview.skipped_blank > 0 && <span className="text-gray-500">{preview.skipped_blank} blank rows ignored</span>}
              {!preview.has_header && <span className="text-amber-700">No header row detected — columns assumed in default order</span>}
              {preview.unmapped_columns?.length > 0 && <span className="text-gray-500">Ignored columns: {preview.unmapped_columns.join(', ')}</span>}
            </div>
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-gray-50"><tr>{shown.map((c) => <th key={c} className="text-left px-2 py-1.5 font-semibold text-gray-600 whitespace-nowrap">{LABEL[c]}</th>)}</tr></thead>
                <tbody>
                  {preview.rows.map((r, i) => (
                    <tr key={i} className={`border-t ${r.duplicate ? 'bg-amber-50 text-gray-400 line-through' : ''}`} data-testid={`paste-row-${i}`}>
                      {shown.map((c) => <td key={c} className="px-2 py-1 whitespace-nowrap max-w-[220px] truncate">{r[c]}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-between gap-2">
              <Button variant="outline" onClick={() => setPreview(null)}>Back</Button>
              <Button onClick={() => run(true)} disabled={busy || preview.total - preview.duplicates === 0} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="paste-import-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Add ${preview.total - preview.duplicates} website${preview.total - preview.duplicates === 1 ? '' : 's'}`}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
